import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon, CheckIcon, PlusIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useStore } from '../contexts/StoreContext';
import { Gallery } from '../components/vehicle/Gallery';
import { AvailabilityBadge } from '../components/AvailabilityBadge';
import { OfferStatus } from '../components/OfferStatus';
import { FinanceConditions } from '../components/FinanceConditions';
import { CircularTable } from '../components/simulator/CircularTable';
import { eligibleLines, managementFeeText } from '../utils/circular';
import { InquiryForm } from '../components/InquiryForm';
import { WhatsAppButton } from '../components/WhatsAppButton';
import { getQuickQuote } from '../utils/finance';
import { vehicleBrandName, vehicleText } from '../utils/catalog';
import { formatARS, formatDate, formatPct } from '../utils/format';
function Spec({
  label,
  value
}: {
  label: string;
  value: string | null;
}) {
  return <div className="py-2.5 sm:grid sm:grid-cols-[180px_1fr] sm:gap-4" data-id="element-1397" data-mp="2lf6fdop4bndm">
      <dt className="text-sm text-mist" data-id="element-1398" data-mp="2hecpips47yi3">{label}</dt>
      <dd className={`text-sm ${value ? 'text-white' : 'italic text-mist/70'}`} data-id="element-1399" data-mp="2hdkx1nttegw1">{value ?? 'No disponible'}</dd>
    </div>;
}
function ListBlock({
  items
}: {
  items: string[];
}) {
  if (!items.length) return <p className="text-sm italic text-mist/70" data-id="element-1400" data-mp="2qfkak65mf2u8">No disponible</p>;
  return <ul className="grid gap-2 sm:grid-cols-2" data-id="element-1401" data-mp="22qhwt9e3xu50">
      {items.map(i => <li key={i} className="flex items-start gap-2 text-sm" data-id="element-1402" data-mp="1xqn36y2y6qg9">
          <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-neon" aria-hidden data-id="element-1403" data-mp="1xoag66ic1pwc" />
          {i}
        </li>)}
    </ul>;
}
export function VehicleDetail() {
  const {
    id
  } = useParams();
  const {
    vehicles,
    brands,
    compareIds,
    toggleCompare,
    catalogSync
  } = useStore();
  const vehicle = vehicles.find(v => v.id === id && v.published);
  const quote = useMemo(() => vehicle ? getQuickQuote(vehicle, brands) : null, [vehicle, brands]);
  if (!vehicle && catalogSync.status === 'loading') {
    return <div className="container-x py-24 text-center" role="status" data-id="element-1404" data-mp="3k186wz2beyq5">
        <p className="font-display text-xl font-bold" data-id="element-1405" data-mp="3jzdkfgbeayjy">Cargando unidad…</p>
        <p className="mt-2 text-sm text-mist" data-id="element-1406" data-mp="3osznqhvo5q33">Estamos consultando el inventario actualizado.</p>
      </div>;
  }
  if (!vehicle) {
    return <div className="container-x py-24 text-center" data-id="element-1407" data-mp="3svcun9m5ju1g">
        <h1 className="font-display text-2xl font-bold" data-id="element-1408" data-mp="2w4kkeslwgw7l">Unidad no disponible</h1>
        <p className="mt-2 text-mist" data-id="element-1409" data-mp="311kiag8lisnm">Puede que haya sido dada de baja o que el enlace sea incorrecto.</p>
        <Link to="/catalogo" className="btn btn-primary mt-6" data-id="element-1410" data-mp="352w8onzanhwv">Volver al catálogo</Link>
      </div>;
  }
  const brand = brands.find(b => b.id === vehicle.brandId);
  const title = `${vehicleBrandName(vehicle, brands)} ${vehicle.model}`;
  const text = vehicleText(vehicle);
  const inCompare = compareIds.includes(vehicle.id);
  return <div className="container-x py-6 lg:py-10" data-id="element-1411" data-mp="pew59rpq6bss">
      <Link to="/catalogo" className="inline-flex items-center gap-1.5 text-sm text-mist hover:text-white" data-id="element-1412" data-mp="zgzz9wubsye9">
        <ArrowLeftIcon className="h-4 w-4" aria-hidden data-id="element-1413" data-mp="uiopfm416nw1" /> Catálogo
      </Link>

      <div className="mt-4 grid gap-8 lg:grid-cols-[1fr_400px]" data-id="element-1414" data-mp="pqf30g24k5yi">
        <div className="min-w-0" data-id="element-1415" data-mp="pokekrrn9vpo">
          <Gallery images={vehicle.images} alt={title} data-id="element-1416" data-mp="lq4w916idbil" />

          <div className="mt-6 flex flex-wrap items-center gap-2" data-id="element-1417" data-mp="lniob0fpgtzu">
            <AvailabilityBadge value={vehicle.availability} label={text.availability} data-id="element-1418" data-mp="grtlmo8e2wzb" />
            <span className="text-xs text-mist" data-id="element-1419" data-mp="bq4brbwwkpct">{[text.segment, text.bodyType, text.year && `Año/modelo ${text.year}`].filter(Boolean).join(' · ')}</span>
          </div>
          <h1 className="mt-2 font-display text-3xl font-black tracking-tight sm:text-4xl" data-id="element-1420" data-mp="2qqzkbhe9moe">{title}</h1>
          <p className="text-lg text-mist" data-id="element-1421" data-mp="216lt8o0gkjgq">{vehicle.version}</p>

          <section className="mt-10" aria-labelledby="s-motor" data-id="element-1422" data-mp="213zikfi9tnua">
            <h2 id="s-motor" className="font-display text-lg font-bold" data-id="element-1423" data-mp="1x5tgvlwahyg8">Motorización</h2>
            <dl className="mt-2 divide-y divide-line" data-id="element-1424" data-mp="248qc0utsglml">
              <Spec label="Motor" value={vehicle.engine} data-id="element-1425" data-mp="296rrl5s0a2tb" />
              <Spec label="Transmisión" value={vehicle.transmission} data-id="element-1426" data-mp="2969nxo30kmbd" />
              <Spec label="Consumo" value={vehicle.consumption} data-id="element-1427" data-mp="1v7s25vwo5jw3" />
            </dl>
          </section>

          <section className="mt-10" aria-labelledby="s-seg" data-id="element-1428" data-mp="1z7u6ydxpatfl">
            <h2 id="s-seg" className="mb-3 font-display text-lg font-bold" data-id="element-1429" data-mp="1lh78mmvur5k1">Seguridad</h2>
            <ListBlock items={vehicle.safety} data-id="element-1430" data-mp="1lfdz8ztuu78c" />
          </section>

          <section className="mt-10" aria-labelledby="s-eq" data-id="element-1431" data-mp="1q9i7rnirqnbj">
            <h2 id="s-eq" className="mb-3 font-display text-lg font-bold" data-id="element-1432" data-mp="1bjjcbsillpzr">Equipamiento</h2>
            <ListBlock items={vehicle.equipment} data-id="element-1433" data-mp="1ghule112jwgx" />
          </section>

          <section className="mt-10" aria-labelledby="s-gar" data-id="element-1434" data-mp="1gg1fwp26ziai">
            <h2 id="s-gar" className="font-display text-lg font-bold" data-id="element-1435" data-mp="12jnvp869x2sc">Garantía y disponibilidad</h2>
            <dl className="mt-2 divide-y divide-line" data-id="element-1436" data-mp="17g4k83hdxj5y">
              <Spec label="Garantía" value={vehicle.warranty} data-id="element-1437" data-mp="nvmdr6e0a7y8" />
              <Spec label="Disponibilidad" value={text.availability || null} data-id="element-1438" data-mp="nvw793y9ex8o" />
              <Spec label="Vigencia de la oferta" value={quote ? `Hasta el ${formatDate(quote.ctx.offer.validUntil)}` : null} data-id="element-1439" data-mp="ixuoyl8nr1y9" />
              <Spec label="Datos actualizados" value={formatDate(vehicle.updatedAt)} data-id="element-1440" data-mp="wu7uqx3txeww" />
            </dl>
          </section>

          {quote && <section className="mt-10" aria-labelledby="s-fin" data-id="element-1441" data-mp="16tqebcftuyof">
              <h2 id="s-fin" className="font-display text-lg font-bold" data-id="element-1442" data-mp="16s5k5nxfdhr3">Condiciones financieras</h2>
              <div className="mt-2" data-id="element-1443" data-mp="11tkqy8glbyc5">
                <OfferStatus offer={quote.ctx.offer} source={quote.ctx.source} data-id="element-1444" data-mp="1gkl24l8q26bz" />
              </div>
              <div className="mt-3" data-id="element-1445" data-mp="1bkyquhcgf05t">
                {quote.ctx.circular ? <>
                    <p className="mb-3 text-sm text-mist" data-id="element-1446" data-mp="1pm1dav7d2ch9">
                      {quote.ctx.circular.circular.lender} · {quote.ctx.circular.circular.reference}. Cuotas con IVA incluido, sin seguro automotor. {managementFeeText(quote.ctx.circular.circular)}
                    </p>
                    <CircularTable circular={quote.ctx.circular.circular} lines={eligibleLines(quote.ctx.circular.circular, vehicle)} selected={{
                lineId: quote.ctx.circular.line.id,
                term: quote.ctx.circular.row.term
              }} data-id="element-1447" data-mp="n726ijh0cp7h" />
                  </> : <FinanceConditions offer={quote.ctx.offer} source={quote.ctx.source} data-id="element-1448" data-mp="10yqhgjfit7mv" />}
              </div>
            </section>}
        </div>

        <aside className="space-y-5 lg:sticky lg:top-24 lg:self-start" data-id="element-1449" data-mp="1ay7ksu7av9fo">
          <div className="card p-5" data-id="element-1450" data-mp="1ez9llfgv7tjr">
            <p className="text-xs text-mist" data-id="element-1451" data-mp="1exga9mvb4l5f">Precio de contado</p>
            <p className="font-display text-3xl font-black tracking-tight" data-id="element-1452" data-mp="3cp11xlwxryps">{formatARS(vehicle.price)}</p>
            {quote?.sim && <dl className="mt-4 grid grid-cols-2 gap-3 border-t border-line pt-4 text-sm" data-id="element-1453" data-mp="3gqmnrwrg7nc4">
                <div data-id="element-1454" data-mp="397dc1wvgzcjw"><dt className="text-[11px] text-mist" data-id="element-1455" data-mp="3986dqjfknbv6">Anticipo requerido</dt><dd className="font-semibold" data-id="element-1456" data-mp="34fd71q0jltgg">{formatARS(quote.down)}</dd></div>
                <div data-id="element-1457" data-mp="2zgs8dv0xvhu4"><dt className="text-[11px] text-mist" data-id="element-1458" data-mp="2zgis4z5p4p1m">Capital a financiar</dt><dd className="font-semibold" data-id="element-1459" data-mp="2zh1zjlxpzv1e">{formatARS(quote.principal)}</dd></div>
                <div data-id="element-1460" data-mp="2vgi5ir8g17jh"><dt className="text-[11px] text-mist" data-id="element-1461" data-mp="2vgrtk99xh4mj">Cuotas estimadas</dt><dd className="font-semibold text-neon" data-id="element-1462" data-mp="3s5zhtp1xlru5">{quote.term} × {formatARS(quote.sim.firstInstallment)}</dd></div>
                <div data-id="element-1463" data-mp="3n7fv3mfikc0q"><dt className="text-[11px] text-mist" data-id="element-1464" data-mp="3n7fqta8hdpux">TNA / CFT</dt><dd className="font-semibold" data-id="element-1465" data-mp="3j4rir4rlcmz0">{formatPct(quote.ctx.offer.tna)} / {formatPct(quote.ctx.offer.cft, 'N/I')}</dd></div>
              </dl>}
            {quote && <div className="mt-3" data-id="element-1466" data-mp="3e5ez0sw0nw0n"><OfferStatus offer={quote.ctx.offer} source={quote.ctx.source} compact data-id="element-1467" data-mp="3e811wf7ypncj" /></div>}
            <div className="mt-5 grid gap-2" data-id="element-1468" data-mp="27njhgsc8rmz2">
              <Link to={`/simulador?v=${vehicle.id}`} className="btn btn-primary" data-id="element-1469" data-mp="22q9qh897f394">SIMULAR FINANCIACIÓN</Link>
              <WhatsAppButton vehicle={vehicle} data-id="element-1470" data-mp="6mbqcm3krneb" />
              <button onClick={() => {
              const r = toggleCompare(vehicle.id);
              if (r === 'full') toast.error('Podés comparar hasta 3 vehículos.');
            }} className="btn btn-ghost" aria-pressed={inCompare} data-id="element-1471" data-mp="6pr6ls8zj2hb">
                {inCompare ? <CheckIcon className="h-4 w-4" data-id="element-1472" data-mp="pdcrngs63v11" /> : <PlusIcon className="h-4 w-4" data-id="element-1473" data-mp="peeeeiijq5yl" />}
                {inCompare ? 'En el comparador' : 'Agregar al comparador'}
              </button>
            </div>
          </div>

          <div className="card p-5" data-id="element-1474" data-mp="fn1m5psu5ds1">
            <h2 className="font-display text-lg font-bold" data-id="element-1475" data-mp="flyw1k3c7fd6">Consultar por esta unidad</h2>
            <p className="mb-4 mt-1 text-sm text-mist" data-id="element-1476" data-mp="klbhq1ih2ge6">Un asesor comercial te responde con disponibilidad y condiciones actualizadas.</p>
            <InquiryForm channel="vehiculo" vehicle={vehicle} allowQuote idPrefix="vd" data-id="element-1477" data-mp="1d370ln9d8zg0" />
          </div>
        </aside>
      </div>
    </div>;
}